import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { mdiChevronDown, mdiPalette } from '@mdi/js'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover'
import { AppIcon } from '../ui/icon'
import type { ThematicCatalogue, ThematicEntry } from './api'
import { pickLabel } from './colour'
import type { PickSource } from './useThematicChoice'

interface ThematicPickerProps {
  catalogue: ThematicCatalogue | undefined
  /** The chosen key, null for "Aucune". */
  value: string | null
  onChange: (key: string | null, source: PickSource, coverage?: { covered?: number, total?: number }) => void
}

function EntryButton({
  entry, active, onPick, lang,
}: {
  entry: ThematicEntry
  active: boolean
  onPick: () => void
  lang: string
}) {
  const { t } = useTranslation('thematics')
  const available = entry.available !== false
  const cov = entry.coverage
  return (
    <button
      type="button"
      data-testid={`thematic-entry-${entry.key}`}
      data-available={available}
      disabled={!available}
      aria-pressed={active}
      title={!available && entry.reason ? t(`reason.${entry.reason}`) : undefined}
      onClick={onPick}
      className={`flex w-full items-center justify-between gap-3 rounded-md px-2 py-1.5 text-left text-sm ${
        active ? 'bg-accent' : 'hover:bg-accent/60'
      } ${available ? '' : 'cursor-not-allowed opacity-45'}`}
    >
      <span>{pickLabel(entry.name, lang, entry.code ?? entry.key)}</span>
      <span className="text-xs text-muted-foreground" data-testid={`thematic-entry-${entry.key}-coverage`}>
        {cov ? `${cov.covered}/${cov.total}` : ''}
      </span>
    </button>
  )
}

/**
 * The "Colorer par" selector (spec 2026-10-06-10): suggestions first, every
 * eligible entry grouped by category, a search, the coverage of each entry and
 * "Aucune". A suggestion with no data stays listed, greyed out.
 */
export function ThematicPicker({ catalogue, value, onChange }: ThematicPickerProps) {
  const { t, i18n } = useTranslation('thematics')
  const lang = i18n.language || 'en'
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')

  const all = useMemo(() => {
    const out: ThematicEntry[] = [...(catalogue?.suggested ?? [])]
    for (const g of catalogue?.groups ?? []) out.push(...(g.entries ?? []))
    return out
  }, [catalogue])
  const current = all.find((e) => e.key === value)
  const q = query.trim().toLowerCase()
  const matches = (e: ThematicEntry) => !q || pickLabel(e.name, lang, e.code).toLowerCase().includes(q)

  const pick = (entry: ThematicEntry, source: PickSource) => {
    onChange(entry.key ?? null, q ? 'search' : source, entry.coverage)
    setOpen(false)
    setQuery('')
  }

  const suggested = (catalogue?.suggested ?? []).filter(matches)
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button type="button" variant="outline" size="sm" data-testid="thematic-picker-trigger">
          <AppIcon path={mdiPalette} size="sm" className="mr-1" />
          <span className="text-muted-foreground">{t('colour_by')}</span>
          <span className="ml-1 font-medium" data-testid="thematic-picker-current">
            {current ? pickLabel(current.name, lang, current.code) : value ? value.replace(/^(property|definition):/, '') : t('none')}
          </span>
          <AppIcon path={mdiChevronDown} size="sm" tone="muted" className="ml-1" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-80 p-2" data-testid="thematic-picker">
        <p className="px-2 pb-2 text-xs text-muted-foreground">{t('help')}</p>
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t('search')}
          data-testid="thematic-picker-search"
          className="mb-2 h-8"
        />
        <div className="max-h-80 space-y-2 overflow-y-auto">
          <button
            type="button"
            data-testid="thematic-entry-none"
            onClick={() => { onChange(null, 'list'); setOpen(false) }}
            className={`flex w-full items-center rounded-md px-2 py-1.5 text-left text-sm ${value === null ? 'bg-accent' : 'hover:bg-accent/60'}`}
          >
            {t('none')}
          </button>
          {suggested.length > 0 && (
            <div data-testid="thematic-picker-suggested">
              <div className="px-2 pb-1 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">{t('suggested')}</div>
              {suggested.map((e) => (
                <EntryButton key={e.key} entry={e} lang={lang} active={e.key === value} onPick={() => pick(e, 'suggestion')} />
              ))}
            </div>
          )}
          {(catalogue?.groups ?? []).map((g) => {
            const entries = (g.entries ?? []).filter(matches)
            if (entries.length === 0) return null
            return (
              <div key={g.category_code || 'other'} data-testid={`thematic-picker-group-${g.category_code || 'other'}`}>
                <div className="px-2 pb-1 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                  {pickLabel(g.category_name, lang, g.category_code || t('other'))}
                </div>
                {entries.map((e) => (
                  <EntryButton key={e.key} entry={e} lang={lang} active={e.key === value} onPick={() => pick(e, 'list')} />
                ))}
              </div>
            )
          })}
        </div>
      </PopoverContent>
    </Popover>
  )
}
